import React, { useEffect, useState } from "react";
import { Button, DatePicker, Input, Select, Space, Tag, Typography, message, theme } from "antd";
import { ThunderboltOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { errorText } from "../api";
import { formatCents, formatDate } from "../shared/money";
import MoneyInput from "../shared/MoneyInput";
import FilterToolbar from "../shared/FilterToolbar";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import { useIsMobile } from "../../../../design-system/useMediaQuery";
import { STATUS_COLORS, STATUS_LABELS } from "./constants";
import { autoMatch, listTransactions } from "./api";
import MatchDrawer from "./MatchDrawer";

/** Signed amount: "+" prefix, green in / red out (token colors). */
const SignedAmount = ({ value, large }) => {
  const { token } = theme.useToken();
  return (
    <span
      className={`acct-num acct-num--strong${large ? " acct-amount-lg" : ""}`}
      style={{ color: value < 0 ? token.colorError : token.colorSuccess }}
    >
      {value > 0 ? "+" : ""}
      {formatCents(value)}
    </span>
  );
};

/** Transaction list with filters; a row opens the match drawer. */
const TransactionsTab = ({
  accounts,
  canWrite,
  onChanged,
  initialStatus = "unmatched,suggested"
}) => {
  const { t } = useTranslation("accounting");
  const isMobile = useIsMobile();
  const [filters, setFilters] = useState({
    account: "",
    status: initialStatus,
    q: "",
    from: "",
    to: "",
    min: null,
    max: null
  });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ transactions: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [drawerId, setDrawerId] = useState(null);
  const [autoBusy, setAutoBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      for (const [k, v] of Object.entries(filters))
        if (v !== "" && v !== null && v !== undefined) params[k] = v;
      const res = await listTransactions(params);
      setData(res.data);
    } catch (err) {
      message.error(errorText(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [page, filters.account, filters.status, filters.from, filters.to, filters.min, filters.max]);

  const runAutoMatch = async () => {
    setAutoBusy(true);
    try {
      const res = await autoMatch(null);
      message.success(
        t("{{matched}} matched automatically, {{suggested}} with suggestions", res.data)
      );
      load();
      onChanged?.();
    } catch (err) {
      message.error(errorText(err));
    } finally {
      setAutoBusy(false);
    }
  };

  const accountName = id => accounts.find(a => a._id === id)?.name || "";

  const columns = [
    {
      title: t("Date"),
      dataIndex: "bookingDate",
      width: 100,
      render: v => <span className="acct-num">{formatDate(v)}</span>
    },
    {
      title: t("Counterpart"),
      dataIndex: "counterpartName",
      ellipsis: true,
      mobile: "title",
      render: (v, r) => (
        <div className="acct-wrap">
          <div>{v || <Typography.Text type="secondary">–</Typography.Text>}</div>
          {r.counterpartIban ? (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {r.counterpartIban}
            </Typography.Text>
          ) : null}
        </div>
      )
    },
    {
      title: t("Amount"),
      dataIndex: "amount",
      width: 130,
      align: "end",
      mobile: "subtitle",
      render: v => <SignedAmount value={v} large={isMobile} />
    },
    {
      title: t("Purpose"),
      dataIndex: "purpose",
      ellipsis: { showTitle: true },
      render: (v, r) => (
        <div className="acct-wrap">
          <div>{v}</div>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {[r.bookingText, accounts.length > 1 ? accountName(r.bankAccount) : ""]
              .filter(Boolean)
              .join(" · ")}
          </Typography.Text>
        </div>
      )
    },
    {
      title: t("Status"),
      dataIndex: "matchStatus",
      width: 120,
      render: v => <Tag color={STATUS_COLORS[v]}>{t(STATUS_LABELS[v] || v)}</Tag>
    },
    {
      title: t("Suggestion"),
      dataIndex: "suggestions",
      ellipsis: true,
      render: (list, r) => {
        if (["matched", "split"].includes(r.matchStatus)) {
          return (
            <Typography.Text type="secondary" ellipsis>
              {(r.matches || []).map(m => m.label || m.account || m.type).join(", ")}
            </Typography.Text>
          );
        }
        const top = (list || [])[0];
        if (!top) return <Typography.Text type="secondary">–</Typography.Text>;
        return (
          <Space size={4} wrap>
            <Tag
              color={top.confidence >= 0.95 ? "green" : top.confidence >= 0.8 ? "gold" : "default"}
            >
              {Math.round(top.confidence * 100)} %
            </Tag>
            <Typography.Text ellipsis style={{ maxWidth: 220 }}>
              {top.label || top.type}
            </Typography.Text>
          </Space>
        );
      }
    }
  ];

  return (
    <div>
      <FilterToolbar
        search={
          <Input.Search
            allowClear
            placeholder={t("Search purpose, name, IBAN")}
            value={filters.q}
            onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
            onSearch={() => {
              setPage(1);
              load();
            }}
          />
        }
        activeCount={
          [
            filters.account,
            filters.status !== initialStatus,
            filters.from,
            filters.min !== null,
            filters.max !== null
          ].filter(Boolean).length
        }
        filters={
          <>
            <Select
              allowClear
              placeholder={t("All accounts")}
              value={filters.account || undefined}
              onChange={v => {
                setPage(1);
                setFilters(f => ({ ...f, account: v || "" }));
              }}
              options={accounts.map(a => ({ value: a._id, label: a.name }))}
            />
            <Select
              value={filters.status}
              onChange={v => {
                setPage(1);
                setFilters(f => ({ ...f, status: v }));
              }}
              options={[
                { value: "unmatched,suggested", label: t("Open") },
                { value: "suggested", label: t("Suggested") },
                { value: "matched,split", label: t("Matched") },
                { value: "ignored", label: t("Ignored") },
                { value: "", label: t("All") }
              ]}
            />
            <DatePicker.RangePicker
              format="DD.MM.YYYY"
              onChange={range => {
                setPage(1);
                setFilters(f => ({
                  ...f,
                  from: range?.[0] ? range[0].format("YYYY-MM-DD") : "",
                  to: range?.[1] ? range[1].format("YYYY-MM-DD") : ""
                }));
              }}
            />
            <MoneyInput
              placeholder={t("min")}
              value={filters.min}
              onChange={v => setFilters(f => ({ ...f, min: v }))}
              addonAfter={null}
            />
            <MoneyInput
              placeholder={t("max")}
              value={filters.max}
              onChange={v => setFilters(f => ({ ...f, max: v }))}
              addonAfter={null}
            />
          </>
        }
        actions={
          canWrite ? (
            <Button icon={<ThunderboltOutlined />} loading={autoBusy} onClick={runAutoMatch}>
              {t("Auto-match")}
            </Button>
          ) : null
        }
      />
      <ResponsiveTable
        rowKey="_id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={data.transactions}
        onRow={r => ({ onClick: () => setDrawerId(r._id), style: { cursor: "pointer" } })}
        pagination={{
          current: page,
          pageSize: 50,
          total: data.total,
          onChange: setPage,
          showSizeChanger: false
        }}
        scroll={{ x: 900 }}
      />
      <MatchDrawer
        id={drawerId}
        accounts={accounts}
        canWrite={canWrite}
        onClose={() => setDrawerId(null)}
        onChanged={() => {
          load();
          onChanged?.();
        }}
      />
    </div>
  );
};

export default TransactionsTab;
